import { Alert } from "@aws-amplify/ui-react"
import { useState } from "react"
import style from "../style/PhotosensitiveWarning.module.css"

type Properties = Readonly<{ warningHeading: string; warningInfo: string; warningText: string }>

/**
 * @description
 *	The photosensitivity warning of the landing. It is mandatory: the Amplify
 *	widget is mounted with its own start screen disabled, so this is the only
 *	place where the warning is shown.
 *
 * @remarks
 *	The whole row is the target, to keep it above the 44 (forty four) pixels
 *	of a comfortable touch target. On the shortest viewports the body of the
 *	warning collapses and only its heading survives, but the detail is always
 *	reachable through the row.
 */
export default function PhotosensitiveWarning({ warningHeading, warningInfo, warningText }: Properties) {
	const [isInfoVisible, setIsInfoVisible] = useState(false)
	return (
		<div className={style["photosensitive-warning"]}>
			<button
				aria-expanded={isInfoVisible}
				className={style["photosensitive-warning-row"]}
				onClick={() => setIsInfoVisible(!isInfoVisible)}
				type="button"
			>
				<span aria-hidden="true" className={style["photosensitive-warning-icon"]}>
					<svg
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="1.8"
						viewBox="0 0 24 24"
					>
						<path d="M12 3.5l9 15.5H3z" />
						<path d="M12 9.5v4.2" />
						<path d="M12 16.6h.01" />
					</svg>
				</span>
				<span className={style["photosensitive-warning-label"]}>
					<span className={style["photosensitive-warning-heading"]}>{warningHeading}</span>
					<span className={style["photosensitive-warning-text"]}>{warningText}</span>
				</span>
				<span
					aria-hidden="true"
					className={
						isInfoVisible
							? `${style["photosensitive-warning-chevron"]} ${style["photosensitive-warning-chevron-open"]}`
							: style["photosensitive-warning-chevron"]
					}
				>
					<svg
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						viewBox="0 0 24 24"
					>
						<path d="M6 9.5l6 6 6-6" />
					</svg>
				</span>
			</button>
			{isInfoVisible && (
				<Alert variation="info" isDismissible={true} onDismiss={() => setIsInfoVisible(false)}>
					{warningInfo}
				</Alert>
			)}
		</div>
	)
}
